import { useMemo } from "react";
import { useAuth } from "../contexts/AuthProvider";
import { useHospitalData } from "../contexts/HospitalDataProvider";
import { useNowEveryMinute } from "../hooks/useNowEveryMinute";
import type { DemoOriginsContext } from "./demoRequestOriginSeed";
import { isNurseSchedulableInDepartment } from "./formatters";
import type { HrisIntegrationType } from "./models";

const TIME_OFF_INTEGRATION: HrisIntegrationType = "time_off";

/**
 * What the demo origins are seeded from, read off the loaded department. `null` when `enabled` is false, so a real
 * department never builds the roster. Deleted with the rest of the demo origins when the real resolver lands.
 */
export const useDemoOriginsContext = (enabled: boolean): DemoOriginsContext | null => {
	const { hospitalData, chosenDepartment, timezone, getNurseFormattedName } = useHospitalData();
	const { userInfo } = useAuth();
	const now = useNowEveryMinute();
	return useMemo<DemoOriginsContext | null>(() => {
		if (!enabled) return null;
		const departmentId = chosenDepartment.department_id;
		const hrisSource = chosenDepartment.hrisIntegrations?.find((integration) => integration.type === TIME_OFF_INTEGRATION)?.source;
		return {
			roster: hospitalData.nurses.filter((nurse) => isNurseSchedulableInDepartment(nurse, departmentId)).sort((a, b) => a.id - b.id),
			currentUser: userInfo.id === undefined ? null : { userId: userInfo.id, person: { nurseId: null, name: userInfo.first_name } },
			hrisSource: hrisSource === undefined ? null : hrisSource,
			ratioLocations: chosenDepartment.locations
				.filter((location) => location.ratioLadder !== undefined && location.ratioLadder.length > 0)
				.map((location) => location.location),
			timezone,
			now: now.setZone(timezone),
			nameOf: (nurse) => getNurseFormattedName(nurse.id),
		};
	}, [enabled, hospitalData.nurses, chosenDepartment, timezone, getNurseFormattedName, userInfo.id, userInfo.first_name, now]);
};
